import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import { getCategory, categories } from "@/mocks/categories";
import { comicsByCategory } from "@/mocks/comics";
import { articles } from "@/mocks/articles";

type SortKey = "hot" | "rating" | "new";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "热度最高" },
  { key: "rating", label: "评分优先" },
  { key: "new", label: "最新连载" },
];

export default function CategoryPage() {
  const { slug } = useParams();
  const [sort, setSort] = useState<SortKey>("hot");

  const cat = getCategory(slug ?? "");

  useEffect(() => {
    setSort("hot");
  }, [slug]);

  const list = useMemo(() => {
    const base = comicsByCategory(slug ?? "");
    if (sort === "rating") return [...base].sort((a, b) => b.rating - a.rating);
    if (sort === "new") return [...base].sort((a, b) => Number(b.year) - Number(a.year));
    return base;
  }, [slug, sort]);

  useEffect(() => {
    if (cat) {
      document.title = `${cat.name}妖精漫画在线看 · 韩国${cat.name}奇幻作品大全 - 妖精漫画堂`;
    }
  }, [cat]);

  if (!cat) {
    return <Navigate to="/" replace />;
  }

  const otherCats = categories.filter((c) => c.slug !== cat.slug);
  const relatedArticles = articles.slice(0, 2);

  return (
    <>
      <PageHero
        eyebrow={cat.en}
        title={`${cat.name}妖精漫画`}
        description={cat.longDesc}
        image={cat.banner}
        crumbs={[
          { label: "首页", to: "/" },
          { label: "漫画分类", to: "/category/otherworld" },
          { label: cat.name },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="container-site grid gap-8 lg:grid-cols-[1fr_290px]">
          <div>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
                {sortOptions.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setSort(o.key)}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors ${sort === o.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-700"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <span className="text-sm text-foreground-500">
                共收录 <span className="font-semibold text-primary-700">{list.length}</span> 部作品
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {list.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>

          <aside className="space-y-8">
            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">其他题材分类</h3>
              <ul className="mt-4 space-y-2">
                {otherCats.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/category/${c.slug}`} className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700">
                      {c.name}
                      <i className="ri-arrow-right-line text-foreground-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">相关专题</h3>
              <ul className="mt-4 space-y-4">
                {relatedArticles.map((a) => (
                  <li key={a.id}>
                    <Link to="/about" className="group flex gap-3">
                      <img src={a.image} alt={a.title} loading="lazy" className="h-16 w-24 shrink-0 rounded object-cover object-top" />
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">{a.title}</p>
                        <p className="mt-1 text-xs text-foreground-400">{a.date} · {a.readTime}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-50 p-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-message-3-line text-lg" />
              </span>
              <p className="mt-3 text-sm font-medium text-foreground-900">想推荐这个题材的宝藏作品？</p>
              <Link to="/contact" className="btn btn-primary mt-3 w-full">
                投稿给我们
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}